// ── SourcesPage — per-book line-movement view ──────────────────────
//
// /game/:date/:teamA/:teamB/sources. Sister route to the GameDetail
// page; shows what's under the "Unabated · N books" aggregate — every
// contributing book's raw lines, how they're moving, and how far each
// sits from consensus.
//
// Data comes from the same SSE the GameDetail page already opened via
// the per-gameKey hub (oddsStreamHub). Navigating between /game/:k
// and /game/:k/sources reuses the connection — no second EventSource,
// no flash of empty state, no double-counting against hydra's 256-cap.
//
// V1 surface:
//   • Filter pills: bet type (ML / Spread / Total), period (Full Game
//     only for V1 — multi-period catalog comes later).
//   • Per-source table: one row per marketSourceId for the current
//     filter. Columns: src#N, away price + fair, home price + fair,
//     Δ vs consensus home fair, age, sparkline of last ~20 implied
//     prob points (away or home — toggled via the marketSide column
//     toggle in the header).
//   • Sort: Δ vs consensus | age | source id.
//   • Stale rows dim past 60s since last update.
//
// Out of scope: per-book name labels (hydra source-registry pending),
// σ-tagging (bookConsensusStats not on the public SSE), per-source
// historical chart (would need odds_lines warehouse query).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { composeGameKeyV2, composeHydraGameKey } from '@/services/analyticsGameKey';
import { useOddsStream } from './useOddsStream';
import { isFullGameResult, lineKeyOf } from '@/services/oddsStreamHub';
import { useConsensusGlow } from '@/hooks/ui/useConsensusGlow';
import { SharpStar } from '@/components/atoms/SharpStar';
import type { OddsStreamLine } from '@/services/oddsStream';
import { SourcesChart } from './SourcesChart';

type BetTypeFilter = 1 | 2 | 3;
type SortMode = 'delta' | 'age' | 'source';

const BET_TYPE_LABELS: Record<BetTypeFilter, string> = {
  1: 'Moneyline',
  2: 'Spread',
  3: 'Total',
};

const PERIOD_TYPE_ID = 1; // Full Game only for V1
const STALE_THRESHOLD_MS = 60_000;

export function SourcesPage() {
  const params = useParams<{ sport?: string; date: string; teamA: string; teamB: string; gameNo?: string }>();
  const sport = params.sport;
  const date = params.date ?? '';
  const teamA = (params.teamA ?? '').toUpperCase();
  const teamB = (params.teamB ?? '').toUpperCase();
  const gameNo = params.gameNo;

  // Match GameDetail's odds-stream key so both pages share one EventSource via
  // the per-gameKey hub. Sport-aware route → the doubleheader-safe v2 key (pins
  // a specific game); legacy route → the 3-seg analytics key (live game).
  const oddsKey = useMemo(
    () =>
      composeGameKeyV2({ sport, date, teamA, teamB, gameNo })
      ?? composeHydraGameKey({ date, teamA, teamB }),
    [sport, date, teamA, teamB, gameNo],
  );

  const odds = useOddsStream(oddsKey);

  const [betType, setBetType] = useState<BetTypeFilter>(1);
  const [sortMode, setSortMode] = useState<SortMode>('delta');
  const [tick, setTick] = useState(0);

  // Re-render every 5s so the age column + stale-dim refresh without
  // waiting on the next SSE event. Cheap (one setState per tick) and
  // only runs while the page is mounted.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);
  void tick;

  // Group the filtered lines by source. snapshot.lines + live `line`
  // events both flow through `linesByKey` in the hub, so this collapses
  // the latest line per (source, betType, periodType, side) tuple into
  // away/home pairs per source.
  const rows = useMemo(() => {
    const bySource = new Map<number, { away?: OddsStreamLine; home?: OddsStreamLine }>();
    for (const line of odds.linesByKey.values()) {
      if (line.betTypeId !== betType) continue;
      if (line.periodTypeId !== PERIOD_TYPE_ID) continue;
      const slot = bySource.get(line.marketSourceId) ?? {};
      if (line.sideIndex === 0) slot.away = line;
      else if (line.sideIndex === 1) slot.home = line;
      bySource.set(line.marketSourceId, slot);
    }
    return Array.from(bySource.entries()).map(([sourceId, pair]) => ({
      sourceId,
      away: pair.away ?? null,
      home: pair.home ?? null,
    }));
  }, [odds.linesByKey, betType]);

  // Per-source fair-prob deltas vs consensus. Hub holds the latest
  // consensus[]; we pick the matching betType + Full-Game entry. Books
  // not contributing to the consensus tuple show delta = null (we
  // don't show their de-vigged fair from the snapshot.consensus
  // contributions array in V1; the row's price/impliedProb columns
  // are enough to spot dislocators).
  // Full-Game RESULT row only — the soccer 'advance' rows share
  // periodTypeId 1 and must not become the delta reference.
  const consensusFor = useMemo(() => {
    return odds.latestConsensus.find(isFullGameResult);
  }, [odds.latestConsensus]);
  // Frame recency for the sharp star's gold→grey fade (bucket-level).
  const consensusGlow = useConsensusGlow(odds.consensusTrends.get('1|result') ?? null);

  const now = Date.now();
  void odds.lastHeartbeatMs; // tick reference

  const enriched = rows.map((row) => {
    // De-vig per-book by Σ-impliedProb halving (Power method's simplest
    // form). When only one side is present, fair == implied. Mirrors
    // what the snapshot.consensus contributions array already does
    // server-side — done locally so live `line` events update deltas
    // without waiting on the next consensus event.
    const awayImplied = row.away?.impliedProbCents ?? null;
    const homeImplied = row.home?.impliedProbCents ?? null;
    let awayFair: number | null = null;
    let homeFair: number | null = null;
    if (awayImplied != null && homeImplied != null) {
      const sum = awayImplied + homeImplied;
      if (sum > 0) {
        awayFair = (awayImplied / sum) * 100;
        homeFair = (homeImplied / sum) * 100;
      }
    } else if (awayImplied != null) {
      awayFair = awayImplied;
    } else if (homeImplied != null) {
      homeFair = homeImplied;
    }
    const deltaHome =
      homeFair != null && consensusFor != null
        ? homeFair - consensusFor.homeFairProbCents
        : null;
    const lastUpdateMs = Math.max(
      row.away?.lastUpdateMs ?? 0,
      row.home?.lastUpdateMs ?? 0,
    );
    const ageMs = lastUpdateMs > 0 ? now - lastUpdateMs : null;
    return {
      ...row,
      awayFair,
      homeFair,
      deltaHome,
      ageMs,
    };
  });

  const sorted = enriched.slice().sort((a, b) => {
    if (sortMode === 'delta') {
      const da = a.deltaHome == null ? -Infinity : Math.abs(a.deltaHome);
      const db = b.deltaHome == null ? -Infinity : Math.abs(b.deltaHome);
      return db - da;
    }
    if (sortMode === 'age') {
      const aa = a.ageMs ?? Infinity;
      const bb = b.ageMs ?? Infinity;
      return aa - bb;
    }
    return a.sourceId - b.sourceId;
  });

  const gameDetailHref = sport
    ? `/game/${sport}/${date}/${teamA}/${teamB}${gameNo ? `/${gameNo}` : ''}`
    : `/game/${date}/${teamA}/${teamB}`;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={gameDetailHref}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          >
            <ArrowLeft size={14} />
            <span>{teamA} @ {teamB} &middot; {date}</span>
          </Link>
          <h1 className="text-foreground mt-1 text-lg font-semibold">
            Per-source line movement
          </h1>
        </div>
        <div className="text-muted-foreground font-mono text-xs">
          {odds.status === 'open' && consensusFor && (
            <>
              Consensus (Full Game):
              <span
                className="text-foreground ml-1.5 font-semibold"
                title={
                  consensusFor.sharpBookUsed == null
                    ? undefined
                    : consensusFor.sharpBookUsed
                      ? 'Pinnacle-anchored (sharp) — src 7 weighted 4× the field'
                      : 'Field books only — src 7 had no viable quote or was struck'
                }
              >
                {Math.round(consensusFor.awayFairProbCents)}% / {Math.round(consensusFor.homeFairProbCents)}%
              </span>
              <SharpStar
                sharp={consensusFor.sharpBookUsed}
                recency={consensusGlow?.away.recency ?? 0}
                className="text-[8px]"
              />
              <span className="ml-1.5 opacity-70">
                · {consensusFor.contributingBooks} books
              </span>
            </>
          )}
          {odds.status !== 'open' && <span>SSE: {odds.status}</span>}
        </div>
      </div>

      {/* Filter + sort pills */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="flex gap-1">
          {([1, 2, 3] as BetTypeFilter[]).map((bt) => (
            <button
              key={bt}
              type="button"
              onClick={() => setBetType(bt)}
              className={`rounded-md px-3 py-1.5 font-semibold transition-colors ${
                betType === bt
                  ? 'bg-blue-600 text-white'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {BET_TYPE_LABELS[bt]}
            </button>
          ))}
        </div>
        <span className="text-muted-foreground/40">·</span>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Sort:</span>
          {(['delta', 'age', 'source'] as SortMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setSortMode(m)}
              className={`rounded-md px-2 py-1 transition-colors ${
                sortMode === m
                  ? 'text-foreground bg-muted'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {m === 'delta' ? 'Δ vs consensus' : m === 'age' ? 'Freshest' : 'Source id'}
            </button>
          ))}
        </div>
      </div>

      {/* Live per-source chart — one line per book, home implied prob
          on Y, last 30 min on X. Same filter pills drive it. */}
      <SourcesChart
        filterKey={`${betType}|${PERIOD_TYPE_ID}|1`}
        sourceIds={rows
          .filter((r) => r.home)
          .map((r) => r.sourceId)}
        pointsFor={(sourceId) => {
          const row = rows.find((r) => r.sourceId === sourceId);
          if (!row?.home) return [];
          return odds.sparklines.get(lineKeyOf(row.home)) ?? [];
        }}
      />

      {/* Table */}
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <table className="w-full border-collapse">
          <thead className="bg-muted/50 border-border border-b">
            <tr className="text-muted-foreground text-left text-[10px] font-semibold uppercase tracking-wider">
              <th className="w-20 px-3 py-2">Source</th>
              <th className="w-28 px-3 py-2 text-right">Away (px / fair)</th>
              <th className="w-28 px-3 py-2 text-right">Home (px / fair)</th>
              <th className="w-24 px-3 py-2 text-right" title="Home fair − consensus home fair">Δ home</th>
              <th className="w-20 px-3 py-2 text-right">Age</th>
              <th className="w-32 px-3 py-2 text-right">Sparkline (home)</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted-foreground py-8 text-center text-xs">
                  {odds.status === 'open'
                    ? `No ${BET_TYPE_LABELS[betType].toLowerCase()} lines reported by any source yet.`
                    : 'Waiting for snapshot…'}
                </td>
              </tr>
            ) : (
              sorted.map((row) => {
                const isStale = row.ageMs != null && row.ageMs > STALE_THRESHOLD_MS;
                const homeSparkKey = row.home ? lineKeyOf(row.home) : null;
                const homeSpark = homeSparkKey ? odds.sparklines.get(homeSparkKey) : null;
                return (
                  <tr
                    key={row.sourceId}
                    className={`border-border/40 border-b font-mono text-xs ${isStale ? 'opacity-40' : ''}`}
                  >
                    <td className="text-foreground px-3 py-2">src#{row.sourceId}</td>
                    <td className="px-3 py-2 text-right">
                      {row.away ? (
                        <span className="text-foreground">
                          {formatAmerican(row.away.price)}
                          <span className="text-muted-foreground ml-1.5">
                            / {row.awayFair != null ? `${Math.round(row.awayFair)}%` : '—'}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {row.home ? (
                        <span className="text-foreground">
                          {formatAmerican(row.home.price)}
                          <span className="text-muted-foreground ml-1.5">
                            / {row.homeFair != null ? `${Math.round(row.homeFair)}%` : '—'}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {row.deltaHome == null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={
                            row.deltaHome > 0.5
                              ? 'text-positive'
                              : row.deltaHome < -0.5
                                ? 'text-negative'
                                : 'text-muted-foreground'
                          }
                        >
                          {row.deltaHome > 0 ? '+' : ''}
                          {row.deltaHome.toFixed(1)}
                        </span>
                      )}
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-right">
                      {row.ageMs == null ? '—' : formatAge(row.ageMs)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {homeSpark && homeSpark.length >= 2 ? (
                        <Sparkline values={homeSpark.map((p) => p.cents)} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="text-muted-foreground text-[10px]">
        Book names (FanDuel, DraftKings, …) aren't mapped yet — sources
        display as <code>src#N</code> until hydra ships its registry.
        Δ home shows where each book's de-vigged home-fair sits vs the
        Full-Game consensus. Sparkline tracks the last 20 implied-prob
        points for the home side per source.
      </p>
    </div>
  );
}

function formatAmerican(price: number): string {
  if (!Number.isFinite(price)) return '—';
  const rounded = Math.round(price);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function formatAge(ms: number): string {
  if (ms < 1_000) return '<1s';
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  return `${Math.floor(ms / 3_600_000)}h`;
}

/** Inline SVG sparkline. Cheap and dependency-free; the existing
 *  lightweight-charts engine is overkill for a 20-point trace. */
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const w = 80;
  const h = 18;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / span) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const trend = last >= first ? 'stroke-positive' : 'stroke-negative';
  return (
    <svg width={w} height={h} className="inline-block align-middle">
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.25}
        className={trend}
      />
    </svg>
  );
}
